// Usage tracking (D-075): which screens and controls players use and where they tap, for the admin Usage page.
// Recorded: the route (codes replaced on the server), a control's stable id (`data-track`) or its short visible label,
// the tap position, and the window size. Never anything typed into a field, chat text, or a hand.
import { normaliseRoute, type TrackEventT } from "@digu/shared";

const FLUSH_MS = 15_000, MAX_QUEUE = 60;
const CONTROL = "[data-track], button, a[href], [role=tab], [role=switch], [role=radio], [role=button], summary, select";
let queue: TrackEventT[] = [];
let timer: ReturnType<typeof setInterval> | null = null;
let lastView = "";

/** A control's name as recorded: its data-track id, else a short label with personal details left out. */
export function controlName(el: Element): string | null {
  // Stable ids first: `data-track`, else the test id most controls already carry (add-time, draw, take, tile-public…).
  const id = el.getAttribute("data-track") ?? el.getAttribute("data-testid");
  if (id) return id;
  // People and places in the label (a seat, a profile row, a club card) are named by what they are, not who.
  if (el.closest(".pl-seat, .prow, .cm-lb-row, [data-person]")) return el.closest(".pl-seat") ? "seat" : "player row";
  const href = el.getAttribute("href");
  if (href && href.startsWith("/")) return `link ${normaliseRoute(href)}`;
  const raw = (el.getAttribute("aria-label") || el.textContent || "").replace(/\s+/g, " ").trim();
  if (!raw) return el.tagName.toLowerCase();
  // Long or comma-joined labels are descriptions (often with names); keep only the first words.
  const short = raw.split(/[,.]/)[0]!.split(" ").slice(0, 4).join(" ");
  return short.slice(0, 40);
}

function push(e: TrackEventT): void {
  queue.push(e);
  if (queue.length >= MAX_QUEUE) flush();
}

export function flush(): void {
  if (!queue.length) return;
  const events = queue.splice(0, MAX_QUEUE);
  try {
    void fetch("/api/track", { method: "POST", credentials: "same-origin", keepalive: true, headers: { "content-type": "application/json" }, body: JSON.stringify({ events }) }).catch(() => undefined);
  } catch { /* tracking never gets in a player's way */ }
}

/** Admin pages are staff work, not player usage (and their lists name players). */
const staff = (route: string): boolean => route === "/admin" || route.startsWith("/admin/");
const size = () => ({ vw: Math.max(200, Math.round(window.innerWidth)), vh: Math.max(200, Math.round(window.innerHeight)) });

/** A screen was shown (called on route changes). Repeats of the same route are ignored. */
export function trackView(path: string): void {
  const route = normaliseRoute(path);
  if (staff(route)) return;
  if (route === lastView) return;
  lastView = route;
  push({ kind: "view", route, ...size(), at: Date.now() });
}

function onClick(ev: MouseEvent): void {
  const t = ev.target instanceof Element ? ev.target.closest(CONTROL) : null;
  if (!t || t.closest("[data-track-off]") || staff(location.pathname)) return;
  const { vw, vh } = size();
  push({
    kind: "click", route: normaliseRoute(location.pathname), target: controlName(t) ?? undefined,
    x: Math.min(1, Math.max(0, ev.clientX / vw)), y: Math.max(0, Math.round(ev.clientY + window.scrollY)),
    vw, vh, dh: Math.round(document.documentElement.scrollHeight), at: Date.now(),
  });
}
function onHide(): void { if (document.visibilityState === "hidden") flush(); }

/** Start for a signed-in player; stop on sign-out. */
export function startTracking(): void {
  if (timer || typeof window === "undefined") return;
  // A page shown inside a frame (the admin heatmap, layout checks) is not a player using the app.
  try { if (window.self !== window.top) return; } catch { return; }
  document.addEventListener("click", onClick, { capture: true, passive: true });
  document.addEventListener("visibilitychange", onHide);
  window.addEventListener("pagehide", flush);
  timer = setInterval(flush, FLUSH_MS);
}
export function stopTracking(): void {
  if (!timer) return;
  flush();
  clearInterval(timer); timer = null; lastView = "";
  document.removeEventListener("click", onClick, { capture: true });
  document.removeEventListener("visibilitychange", onHide);
  window.removeEventListener("pagehide", flush);
}
/** Tests. */
export const _trackQueue = (): TrackEventT[] => queue;
export const _resetTracking = (): void => { stopTracking(); queue = []; };
